/**
 * Authentication operations hook.
 * Handles login, register, and logout operations.
 */

import { useCallback } from 'react'
import type { TokenManager } from '@/services/token-manager'
import { authService, RegisterError, type AuthService } from '@/services/auth.service'
import { type User, AuthError, normalizeUserData } from './types'
import type { TokenClearReason } from '@/services/cross-tab-coordinator'
import { TARGET_LANG_CODES } from '@/constants/languages'

interface UseAuthOperationsParams {
    tokenManager: TokenManager
    setUser: React.Dispatch<React.SetStateAction<User | null>>
    setStatus: React.Dispatch<React.SetStateAction<'signedOut' | 'signedIn' | 'restoring'>>
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    setError: React.Dispatch<React.SetStateAction<string | null>>
    clearAuthData: (
        reason?: TokenClearReason,
        errorDetails?: { errorType?: string; status?: number; message?: string }
    ) => void
    setIsIntentionalLogout: React.Dispatch<React.SetStateAction<boolean>>
    /** Auth service instance (defaults to authService singleton) */
    authService?: AuthService
}

interface UseAuthOperationsReturn {
    login: (username: string, password: string) => Promise<void>
    register: (
        username: string,
        email: string,
        password: string,
        userLang: string,
        targetLang: string,
        inviteKey?: string
    ) => Promise<void>
    logout: () => void
}

/**
 * Hook for authentication operations (login, register, logout).
 */
export const useAuthOperations = ({
    tokenManager,
    setUser,
    setStatus,
    setLoading,
    setError,
    clearAuthData,
    setIsIntentionalLogout,
    authService: authSvc = authService,
}: UseAuthOperationsParams): UseAuthOperationsReturn => {
    // Use the provided service or default to singleton
    const service: AuthService = authSvc ?? authService
    const login = useCallback(
        async (username: string, password: string) => {
            setLoading(true)
            setError(null)
            try {
                // Use authService instead of direct fetch
                const data = await service.login({ username, password })

                // Store tokens
                if (data.access_token) {
                    tokenManager.setAccessToken(data.access_token)
                }
                if (data.refresh_token) {
                    tokenManager.setRefreshToken(data.refresh_token)
                }

                // Get user info (token is already stored, so authenticatedFetch will use it)
                const userData = await service.getCurrentUser()

                const normalizedUserData = normalizeUserData(userData, TARGET_LANG_CODES)

                setUser(normalizedUserData)
                tokenManager.setUserInStorage(normalizedUserData)
                setStatus('signedIn')
                // Reset intentional logout flag on successful login
                setIsIntentionalLogout(false)
            } catch (error) {
                console.error('Login error:', error)
                const errorMessage = error instanceof Error ? error.message : 'Login failed'
                setError(errorMessage)
                // Wrap in AuthError for consistent error handling
                throw new AuthError(
                    errorMessage,
                    'errors.auth.loginFailed',
                    0,
                    error instanceof Error ? error.name : undefined
                )
            } finally {
                setLoading(false)
            }
        },
        [tokenManager, setUser, setStatus, setLoading, setError, setIsIntentionalLogout, service]
    )

    const register = useCallback(
        async (
            username: string,
            email: string,
            password: string,
            userLang: string,
            targetLang: string,
            inviteKey?: string
        ) => {
            setLoading(true)
            setError(null)
            try {
                // Use authService instead of direct fetch
                const registerRequest = {
                    username,
                    email,
                    password,
                    user_lang: userLang,
                    target_langs: [targetLang],
                    ...(inviteKey ? { invite_key: inviteKey } : {}),
                }
                await service.register(registerRequest)

                // After successful registration, automatically log in
                await login(username, password)
            } catch (error) {
                console.error('Registration error:', error)
                const errorMessage = error instanceof Error ? error.message : 'Registration failed'
                setError(errorMessage)
                // Preserve typed RegisterError so callers can route by error code
                // (invite-key validation surfaces inline rather than as a generic alert).
                if (error instanceof RegisterError) {
                    throw error
                }
                // Wrap other errors in AuthError for consistent error handling
                throw new AuthError(
                    errorMessage,
                    'errors.auth.registrationFailed',
                    0,
                    error instanceof Error ? error.name : undefined
                )
            } finally {
                setLoading(false)
            }
        },
        [login, setLoading, setError, service]
    )

    const logout = useCallback(() => {
        // Set intentional logout flag before clearing auth data
        // This prevents ProtectedRoute from treating it as session expiry
        setIsIntentionalLogout(true)
        // Pass 'logout' reason to indicate intentional logout
        clearAuthData('logout')
    }, [clearAuthData, setIsIntentionalLogout])

    return {
        login,
        register,
        logout,
    }
}
